import { DateTime } from 'luxon';
import { DateTimeUnit } from 'luxon/src/datetime';
import { DateRange, DateRangeContext, DateRangePresetType } from '@services/core-services/models';

interface LuxonDateRange {
  start: DateTime;
  end: DateTime;
}

const fromJSDate = (date: Date) => {
  return DateTime.fromJSDate(date);
};

const toJSDate = (date: DateTime) => {
  return date.toJSDate();
};

const toLuxonDateRange = (range: DateRange): LuxonDateRange => {
  const start = fromJSDate(range.start);
  const end = fromJSDate(range.end);

  return {
    start,
    end,
  };
};

const fromLuxonDateRange = (range: LuxonDateRange): DateRange => {
  const start = toJSDate(range.start);
  const end = toJSDate(range.end);

  return {
    start,
    end,
  };
};

const daysElapsed = (range: LuxonDateRange): number => {
  const { start, end } = range;
  return end.diff(start, 'days').days;
};

/**
 * Gets initial application date
 */
export const firstDate = () => {
  return new Date(2023, 11, 1);
};

/**
 * Rounds a Luxon DateTime object up to the nearest half minute after adding 30 seconds.
 * @param dt The Luxon DateTime object.
 */
const roundUpToNearestHalfMinuteLuxon = (dt: DateTime): DateTime => {
  // Add 30 seconds
  const adjustedDt = dt.plus({ seconds: 30 });

  const seconds = adjustedDt.second;
  const milliseconds = adjustedDt.millisecond;

  let secondsToAdd = 0;
  if (seconds > 30 || (seconds === 30 && milliseconds > 0)) {
    // Round up to the next minute
    secondsToAdd = 60 - seconds;
  } else if (seconds > 0 || milliseconds > 0) {
    // Round up to 30 seconds
    secondsToAdd = 30 - seconds;
  }

  // Remove milliseconds before adding seconds to avoid precision issues
  let roundedDt = adjustedDt.set({ millisecond: 0 });
  if (secondsToAdd > 0) {
    roundedDt = roundedDt.plus({ seconds: secondsToAdd });
  }

  return roundedDt;
};

/**
 * Converts a JS Date, rounds it up to the nearest half minute (after adding 30s), and returns a JS Date.
 * @param date The JS Date object.
 */
const roundDateUpToNearestHalfMinute = (date: Date = new Date()): Date => {
  const luxonDt = fromJSDate(date);
  const roundedLuxonDt = roundUpToNearestHalfMinuteLuxon(luxonDt);
  return toJSDate(roundedLuxonDt);
};

/**
 * Gets today's full date range.
 * @param date
 */
export const today = (date: Date = new Date()): DateRange => {
  const start = fromJSDate(date).startOf('day');
  const roundedEnd = roundDateUpToNearestHalfMinute(date);
  return {
    start: toJSDate(start),
    end: roundedEnd,
  };
};

/**
 * Gets day before date based on now or given date.
 * @param date
 */
export const yesterday = (date: Date = new Date()): DateRange => {
  const start = fromJSDate(date).minus({ days: 1 }).startOf('day');
  const end = start.endOf('day');

  return fromLuxonDateRange({ start, end });
};

/**
 * Gets day before yesterday based on now or given date.
 * @param date
 */
export const dayBeforeYesterday = (date: Date = new Date()): DateRange => {
  const start = fromJSDate(date).minus({ days: 1 }).startOf('day');
  const end = start.endOf('day');
  return fromLuxonDateRange({ start, end });
};

/**
 * Converts date to start of day date
 * @param date
 */
export const startOfDay = (date: Date = new Date()) => {
  const d = fromJSDate(date).startOf('day');
  return toJSDate(d);
};

/**
 * Converts date to end of day date
 * @param date
 */
export const endOfDay = (date: Date = new Date()) => {
  const d = fromJSDate(date).endOf('day');
  return toJSDate(d);
};

/**
 * Gets range for months ago based on now or given date. Start and End time are rounded up to the nearest half minute.
 * @param months
 * @param date
 */
export const monthsAgo = (months: number, date: Date = new Date()): DateRange => {
  const initialStart = fromJSDate(date).minus({ months });
  const roundedStart = roundDateUpToNearestHalfMinute(toJSDate(initialStart)); // Round the calculated start date
  const roundedEnd = roundDateUpToNearestHalfMinute(date); // Use rounded end date
  return {
    start: roundedStart,
    end: roundedEnd,
  };
};

/**
 * Gets range for days ago based on now or given date. Start and End time are rounded up to the nearest half minute.
 * @param days
 * @param date
 */
export const daysAgo = (days: number, date: Date = new Date()): DateRange => {
  const initialStart = fromJSDate(date).minus({ days });
  const roundedStart = roundDateUpToNearestHalfMinute(toJSDate(initialStart)); // Round the calculated start date
  const roundedEnd = roundDateUpToNearestHalfMinute(date); // Use rounded end date
  return {
    start: roundedStart,
    end: roundedEnd,
  };
};

/**
 * Weeks to date range based on now or given date.
 * @param date
 * @constructor
 */
export const WTD = (date: Date = new Date()): DateRange => {
  return XTD('week', date);
};

/**
 * "Same period previous week" range.
 * @param range
 * @constructor
 */
export const CWTD = (range: DateRange): DateRange => {
  return CXTD(range, 'weeks', 'week');
};

/**
 * Months to date range based on now or given date.
 * @param date
 * @constructor
 */
export const MTD = (date: Date = new Date()): DateRange => {
  return XTD('month', date);
};

/**
 * "Same period previous month" range.
 * @param range
 * @constructor
 */
export const CMTD = (range: DateRange): DateRange => {
  return CXTD(range, 'months', 'month');
};

/**
 * Quarter to date range based on now or given date.
 * @param date
 * @constructor
 */
export const QTD = (date: Date = new Date()): DateRange => {
  return XTD('quarter', date);
};

/**
 * "Same period previous quarter" rnage
 * @param range
 * @constructor
 */
export const CQTD = (range: DateRange): DateRange => {
  return CXTD(range, 'quarters', 'quarter');
};

/**
 * Year to date range based on now or given date.
 * @param date
 * @constructor
 */
export const YTD = (date: Date = new Date()): DateRange => {
  return XTD('year', date);
};

/**
 * "Same period previous year" range
 * @param range
 * @constructor
 */
export const CYTD = (range: DateRange): DateRange => {
  return CXTD(range, 'years', 'year');
};

/**
 * Generic "x to date" range based on date time unit and provided date (or now).
 * @param unit
 * @param date
 * @constructor
 */
export const XTD = (unit: DateTimeUnit, date: Date = new Date()): DateRange => {
  const start = fromJSDate(date).startOf(unit);
  const roundedEnd = roundDateUpToNearestHalfMinute(date);
  return {
    start: toJSDate(start),
    end: roundedEnd,
  };
};

/**
 * Generic "Same period previous unit" range.
 * @param range
 * @param duration
 * @param unit
 * @constructor
 */
export const CXTD = (range: DateRange, duration: string, unit: DateTimeUnit): DateRange => {
  const { start, end } = toLuxonDateRange(range);
  const compareStart = start.minus({ [duration]: 1 }).startOf(unit);
  const compareEnd = compareStart.plus({ days: daysElapsed({ start, end }) });
  return fromLuxonDateRange({ start: compareStart, end: compareEnd });
};

/**
 * Date range between provided date and now.
 * @param date
 */
export const sinceDate = (date: Date): DateRange => {
  const start = fromJSDate(date).startOf('day');
  const roundedEnd = roundDateUpToNearestHalfMinute();
  return {
    start: toJSDate(start),
    end: roundedEnd,
  };
};

/**
 * Date rang for all time (based on "firstDate" to now.
 */
export const allTime = (): DateRange => {
  const start = fromJSDate(firstDate()).startOf('month').startOf('day');
  const roundedEnd = roundDateUpToNearestHalfMinute();
  return {
    start: toJSDate(start),
    end: roundedEnd,
  };
};

/**
 * Calculates if range is greater than one year.
 * @param range
 */
export const isGreaterThanYear = (range: DateRange) => {
  const start = fromJSDate(range.start);
  const end = fromJSDate(range.end);

  const diff = end.diff(start, 'years').years;
  return diff > 1;
};

/**
 * Gets formatted range for the preset.
 * @param type
 */
export const getFormattedRangeForPreset = (type: DateRangePresetType) => {
  const { range } = getDateRange(type);
  const from = DateTime.fromJSDate(range.start).toFormat('MMM d, yyyy');
  const to = DateTime.fromJSDate(range.end).toFormat('MMM d, yyyy');
  return from === to ? from : `${from} - ${to}`;
};

/**
 * Gets date range based on the preset
 * @param preset
 * @param date
 * @param days
 * @param r
 */
export const getDateRange = (
  preset: DateRangePresetType,
  date = new Date(),
  days?: number,
  r?: DateRange
): DateRangeContext => {
  let range: DateRange;
  let compare: DateRange | undefined = undefined;

  switch (preset) {
    case DateRangePresetType.Today:
      range = today(date);
      compare = yesterday(range.start);
      break;
    case DateRangePresetType.Yesterday:
      range = yesterday(date);
      compare = dayBeforeYesterday(range.start);
      break;
    case DateRangePresetType.D7:
      range = daysAgo(7, date);
      compare = daysAgo(7, range.start);
      break;
    case DateRangePresetType.D30:
      range = daysAgo(30, date);
      compare = daysAgo(30, range.start);
      break;
    case DateRangePresetType.M3:
      range = monthsAgo(3, date);
      compare = monthsAgo(3, range.start);
      break;
    case DateRangePresetType.M6:
      range = monthsAgo(6, date);
      compare = monthsAgo(6, range.start);
      break;
    case DateRangePresetType.M12:
      range = monthsAgo(12, date);
      compare = monthsAgo(12, range.start);
      break;
    case DateRangePresetType.WTD:
      range = WTD(date);
      compare = CWTD(range);
      break;
    case DateRangePresetType.MTD:
      range = MTD(date);
      compare = CMTD(range);
      break;
    case DateRangePresetType.QTD:
      range = QTD(date);
      compare = CQTD(range);
      break;
    case DateRangePresetType.YTD:
      range = YTD(date);
      compare = CYTD(range);
      break;
    case DateRangePresetType.InTheLastXDays:
      range = daysAgo(days!, date);
      compare = sinceDate(range.start);
      break;
    case DateRangePresetType.SinceDate:
      range = sinceDate(date);
      compare = sinceDate(range.start);
      break;
    case DateRangePresetType.Custom:
      range = r!;
      compare = sinceDate(range.start);
      break;
    default:
      range = allTime();
  }

  return {
    range,
    compare,
  };
};
